import { useState } from 'react';
import { toast } from 'sonner';
import { Phone, Loader2, X } from 'lucide-react';

/**
 * "Test Ava on my phone" button + modal (Milestone F).
 * Triggers an outbound Vapi call to the customer's own phone using their
 * latest assistant config. Best 30-second demo of the entire system.
 */
export default function TestAvaButton({ api }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const p = (phone || '').trim();
    if (!p.startsWith('+') || p.length < 8) {
      toast.error('Enter your phone in E.164 format, e.g. +15551234567.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/ava/test-call', { phone: p });
      toast.success('Calling you now. Pick up to chat with Ava live.');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not place test call.');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="ava-test-call-btn" onClick={() => setOpen(true)}
        className="text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-lg px-4 py-2 inline-flex items-center gap-1.5 shadow-lg shadow-blue-500/20">
        <Phone className="w-3.5 h-3.5" /> Test Ava on my phone
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" data-testid="ava-test-call-modal">
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => !busy && setOpen(false)} />
          <div className="relative w-full max-w-md bg-white dark:bg-[#0d0e10] rounded-2xl border border-gray-200 dark:border-white/[0.08] shadow-2xl overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-white/[0.06]">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-base font-semibold text-gray-900 dark:text-white">Test Ava live</h3>
                <p className="text-[12px] text-gray-500 dark:text-slate-400">We'll call you in 5 seconds. Pick up and chat.</p>
              </div>
              <button onClick={() => !busy && setOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1.5 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-5 space-y-3">
              <label className="block text-[11px] uppercase tracking-wide text-gray-500 dark:text-slate-400 font-medium">Your phone number (E.164)</label>
              <input data-testid="ava-test-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+15551234567"
                className="w-full bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] focus:border-blue-400 rounded-xl px-3.5 py-3 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none" />
              <p className="text-[11.5px] text-gray-500 dark:text-slate-400">
                Tip: open a notes app and try asking Ava 3 things — your business hours, a service price, and "I'd like to book". The whole brain runs against you live.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-gray-100 dark:border-white/[0.06] bg-gray-50/50 dark:bg-white/[0.02]">
              <button onClick={() => !busy && setOpen(false)} disabled={busy}
                className="text-sm text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white px-3.5 py-2 rounded-lg disabled:opacity-50">Cancel</button>
              <button data-testid="ava-test-call-submit" onClick={send} disabled={busy}
                className="text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-lg px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50 shadow-lg shadow-blue-500/20">
                {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Dialing...</> : <><Phone className="w-3.5 h-3.5" /> Call me</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
